import React, { useEffect } from 'react'
import { _FORM_ITEM_LAYOUT, _MINERALS, _TAIL_FORM_ITEM_LAYOUT } from '../../../../constants';
import { Label, MineralsFormContainer, MineralsWrapper, StyledForm, StyledFormUpload } from './style';
import { Button, Col, DatePicker, Divider, Form, Input, Row, Select, Upload, UploadProps, message } from 'antd';
import UserLookup from '../../../Common/Form/UserLookup';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useCreateHMA } from '../../../../hooks/admin/useHma';

interface HMAFormProps {
  onReset: boolean;
  onSuccess?: () => void;
}

const { Option } = Select;

const HMAForm: React.FC<HMAFormProps> = ({ onReset, onSuccess }) => {
  const { create: createHMA } = useCreateHMA();
  const [form] = StyledForm.useForm();

  const onFinish = async (values: any) => {
    const {
      client,
      professional,
      reportNumber,
      dateOfAnalysis,
      attachment,
      hairType,
      ...hmaData
    } = values;
    
    console.log('Form values:', values);
    console.log('Attachment value:', attachment);
    
    // Check if attachment exists and has the expected structure
    if (!attachment) {
      message.error('Please upload an attachment file');
      return;
    }
    
    // Handle different attachment structures from Ant Design Upload component
    let file;
    if (attachment.file) {
      // Structure: { file: { originFileObj: File } }
      file = attachment.file.originFileObj;
    } else if (attachment.fileList && attachment.fileList.length > 0) {
      // Structure: { fileList: [{ originFileObj: File }] }
      file = attachment.fileList[0].originFileObj;
    } else if (attachment.originFileObj) {
      // Structure: { originFileObj: File }
      file = attachment.originFileObj;
    } else {
      message.error('Invalid attachment format');
      console.error('Unexpected attachment structure:', attachment);
      return;
    }
    
    if (!file) {
      message.error('Could not extract file from attachment');
      return;
    }
    
    const intHmaData: { [key: string]: number | string | null } = {};
    for (const key in hmaData) {
      if (hmaData.hasOwnProperty(key)) {
        const value = hmaData[key];
        intHmaData[key] = typeof value === 'string' ? parseInt(value, 10) : value;
      }
    }
    
    console.log('Extracted file:', file);
    console.log('Professional ID:', professional);
    console.log('Client ID:', client);
    
    try {
      const res = await createHMA({
        professionalId: professional,
        hmaData: {
          clientId: client,
          reportNumber,
          attachment: file,
          dateOfAnalysis,
          hairType,
          professionalViewable: true, // ✅ Enable by default so client can see it
          ...intHmaData
        }
      });
      console.log('HMA created successfully:', res);
      
      // Wait a moment for the backend to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      message.success('HMA data saved successfully');
      
      // Reset the form
      form.resetFields();
      
      // Call the success callback to refetch data and close modal
      if (onSuccess) {
        console.log('Calling onSuccess callback...');
        await onSuccess();
        console.log('onSuccess callback completed');
      }
    } catch (err) {
      console.error('onSubmitHMA Error', err);
      message.error('Failed to save HMA data');
    }
  };

  const props: UploadProps = {
    name: 'file',
    action: 'https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload',
    headers: {
      authorization: 'authorization-text',
    },
    onChange(info) {
      if (info.file.status !== 'uploading') {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === 'done') {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };


  useEffect(() => {
    if (!onReset) {
      form.resetFields();
    }
  }, [onReset, form]);

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="hma_form"
      onFinish={onFinish}
      initialValues={{ residence: ['zhejiang', 'hangzhou', 'xihu'], prefix: '86' }}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      <p>Import HMA Data</p>
      <Divider style={{ margin: '.75rem 0' }} />
      <Row gutter={50}>
        <Col lg={12}>
          <UserLookup form={form} />
        </Col>
        <Col lg={12}>
          <Form.Item
            labelAlign="left"
            name="reportNumber"
            label="Report number"
            rules={[
              {
                required: true,
                message: 'Report number can\'t be blank',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="dateOfAnalysis"
            label="Date of analysis"
            labelAlign="left"
            rules={[{ required: true, message: 'Date of analysis can\'t be blank' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="hairType"
            labelAlign="left"
            label="Hair Type"
            hasFeedback
            rules={[{ required: true, message: 'Please select a hair type!' }]}
          >
            <Select placeholder="Please select a hair type">
              <Option value="Head">Head</Option>
              <Option value="Pubic">Pubic</Option>
              <Option value="Head and pubic">Head and Pubic</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Divider style={{ margin: '.75rem 0' }} />
      {/* <Form.Item labelAlign="left" label="Attachment:" name="attachment" valuePropName="fileList" getValueFromEvent={normFile} style={{ width: '100%' }}>
        <StyledFormUpload name="attachments">
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">Support for a single or bulk upload.</p>
        </StyledFormUpload>
      </Form.Item> */}
      <Form.Item 
        labelAlign="left" 
        label="Attachment:" 
        name="attachment"
        valuePropName="file"
        getValueFromEvent={(e) => {
          console.log('Upload event:', e);
          // Return the entire event object so we can access it in onFinish
          return e;
        }}
        rules={[
          {
            required: true,
            message: 'Attachment file is required',
          },
        ]}
      >
        <Upload {...props}>
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
      </Form.Item>
      <MineralsWrapper>
        <p>Minerals:</p>
        <MineralsFormContainer>
          {_MINERALS.map((mineral, index) => (
            <div>
              <Label>{mineral.label}</Label>
              <Form.Item
                key={`${mineral.label}_${index}`}
                labelAlign="left"
                name={mineral.name}
                rules={[
                  {
                    required: true,
                    message: `${mineral.label} can\'t be blank`,
                  },
                ]}
              >
                <Input type="number" />
              </Form.Item>
            </div>
          ))}
        </MineralsFormContainer>
      </MineralsWrapper>
      <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm>
  )
}

export default HMAForm